import { Tag, Tooltip } from 'antd'
import { SorterResult } from 'antd/es/table/interface'
import { DrilldownTableExtraColumn } from 'features/drilldown/components/table/config/DrilldownTableConfigContext'
import { DrilldownTableColumnDef } from 'features/drilldown/components/table/config/DrilldownTableSchema'
import {
  DEFAULT_TABLE_PANEL_SHARED_FIELD_DATA,
  DrilldownTablePanelData,
  LINK_TEMPLATE_ASSET,
  LINK_TEMPLATE_COMPONENT,
} from 'features/drilldown/components/table/DrilldownTable'
import { PaginatedRolodexTableRecordType } from 'features/drilldown/components/table/PaginatedRolodexTable'
import {
  buildStatusMap,
  getStatusColor,
} from 'features/fde/advancedServices/inspectionStatusUtils'
import { entriesToAsPipelineStatus } from 'features/fde/advancedServices/rolodexEntryMappings'
import { AsScanStatusType } from 'features/fde/advancedServices/rolodexEntryTypes'
import groupBy from 'lodash/groupBy'
import mapValues from 'lodash/mapValues'
import React, { FC, useMemo } from 'react'
import { GeckoEntry, GeckoPublishingState } from 'types'
import { RolodexEntryType } from 'types/rolodex/rolodexEntries'

export type InspectionTablePanelData = DrilldownTablePanelData

const COLUMN_DEFS: ReadonlyArray<DrilldownTableColumnDef> = [
  {
    title: 'Date',
    field: 'inspection.date',
  },
  {
    title: 'Asset',
    field: 'unit.name',
    linkTemplate: LINK_TEMPLATE_ASSET,
  },
  {
    title: 'Component',
    field: 'component.name',
    linkTemplate: LINK_TEMPLATE_COMPONENT,
  },
  {
    title: 'Type',
    field: 'inspection.inspection_type',
    tagColor: {
      raut: 'volcano',
      rug: 'cyan',
      manual: 'blue',
    },
  },
]

function getScanStatusCounts(
  entries: ReadonlyArray<GeckoEntry>
): Record<string, number> {
  const asPipelineStatusEntry = entriesToAsPipelineStatus(
    entries.filter((e) => e.type === RolodexEntryType.AS_PIPELINE_STATUS)
  )
  const scanInfo = buildStatusMap(asPipelineStatusEntry)
  const groupedScanInfo = groupBy(scanInfo, (status) => status)
  return mapValues(groupedScanInfo, (arr) => arr.length)
}

const ScanStatusCol: FC<{ rec: PaginatedRolodexTableRecordType }> = ({
  rec: { entries },
}) => {
  const statusCounts = useMemo(() => getScanStatusCounts(entries), [entries])

  if (!statusCounts) {
    return <></>
  }
  return (
    <>
      {(Object.entries(statusCounts) as [AsScanStatusType, number][]).map(
        ([status, count]: [AsScanStatusType, number]) => {
          return (
            <Tooltip title={status} key={status}>
              <Tag color={getStatusColor(status)}>{count}</Tag>
            </Tooltip>
          )
        }
      )}
    </>
  )
}

function getInspectionStatus(entries: ReadonlyArray<GeckoEntry>): string {
  const asPipelineStatusEntry = entriesToAsPipelineStatus(
    entries.filter((e) => e.type === RolodexEntryType.AS_PIPELINE_STATUS)
  )
  const scanInfo = buildStatusMap(asPipelineStatusEntry)
  const componentDatasetEntries = entries.filter(
    (e) => e.type === 'component-dataset'
  )
  return computeInspectionStatus(componentDatasetEntries, scanInfo)
}

const InspectionStatusCol: FC<{ rec: PaginatedRolodexTableRecordType }> = ({
  rec: { entries },
}) => {
  const inspectionStatus = useMemo(
    () => getInspectionStatus(entries),
    [entries]
  )

  return (
    <>
      <Tag color='cyan'>{inspectionStatus}</Tag>
    </>
  )
}

function getPendingAndErroringScans(entries: ReadonlyArray<GeckoEntry>): {
  pendingScans: string
  erroringScans: string
} {
  const asPipelineStatusEntry = entriesToAsPipelineStatus(
    entries.filter((e) => e.type === RolodexEntryType.AS_PIPELINE_STATUS)
  )
  const scanInfo = buildStatusMap(asPipelineStatusEntry)
  const scanEntries = Object.entries(scanInfo)
  const pendingScans = scanEntries
    .filter(([, value]) => value === 'wait' || value === 'process')
    .map(([key]) => key)
    .join(', ')
  const erroringScans = scanEntries
    .filter(([, value]) => value === 'error')
    .map(([key]) => key)
    .join(', ')
  return { pendingScans, erroringScans }
}

const PendingScanCol: FC<{ rec: PaginatedRolodexTableRecordType }> = ({
  rec: { entries },
}) => {
  const { pendingScans, erroringScans } = useMemo(
    () => getPendingAndErroringScans(entries),
    [entries]
  )

  return (
    <>
      <Tag color={getStatusColor('process')}>{pendingScans}</Tag>
      <Tag color={getStatusColor('error')}>{erroringScans}</Tag>
    </>
  )
}

export const INSPECTION_TABLE_EXTRA_COL_DEFS: ReadonlyArray<DrilldownTableExtraColumn> =
  [
    {
      id: 'col_insp_status',
      name: 'Inspection Status',
      linked_types: ['component-dataset', RolodexEntryType.AS_PIPELINE_STATUS],
      render: (props) => <InspectionStatusCol {...props} />,
      exportValue: ({ entries }) => getInspectionStatus(entries),
    },
    {
      id: 'col_scan_status',
      name: 'Scan Status',
      linked_types: [RolodexEntryType.AS_PIPELINE_STATUS],
      render: (props) => <ScanStatusCol {...props} />,
      exportValue: ({ entries }) =>
        Object.entries(getScanStatusCounts(entries))
          .map(([status, count]) => `${status}: ${count}`)
          .join(', '),
    },
    {
      id: 'col_pending_scans',
      name: 'Pending Scans',
      linked_types: [RolodexEntryType.AS_PIPELINE_STATUS],
      render: (props) => <PendingScanCol {...props} />,
      exportValue: ({ entries }) => {
        const { pendingScans, erroringScans } =
          getPendingAndErroringScans(entries)
        const parts: string[] = []
        if (pendingScans) parts.push(`Pending: ${pendingScans}`)
        if (erroringScans) parts.push(`Erroring: ${erroringScans}`)
        return parts.join(' | ')
      },
    },
  ]

export const INSPECTION_TABLE_INITIAL_SORTING_OPTIONS: SorterResult<PaginatedRolodexTableRecordType>[] =
  [
    {
      order: 'descend',
      columnKey: 'inspection.date',
    },
  ]

const computeInspectionStatus = (
  componentDatasetEntries: ReadonlyArray<GeckoEntry>,
  scanInfo: Record<string, AsScanStatusType>
): string => {
  const finalizedCdsEntries = componentDatasetEntries.filter(
    (e) => e.raw.data.publishing_state === 'finalized'
  )
  const workingCdsEntries = componentDatasetEntries.filter(
    (e) => e.raw.data.publishing_state === 'working'
  )
  const rawCdsEntries = componentDatasetEntries.filter(
    (e) => e.raw.data.publishing_state === 'raw'
  )

  // Try to match the standard RUG logic
  if (finalizedCdsEntries.length > 0) {
    return GeckoPublishingState.Finalized.name
  }

  if (workingCdsEntries.length > 0) {
    return GeckoPublishingState.Working.name
  }

  if (rawCdsEntries.length > 0) {
    return GeckoPublishingState.Raw.name
  }

  // [AS only]
  // If no CDS entries for RUG logic, Check if there are any scan statuses from the AS pipeline status
  if (scanInfo) {
    const entries = Object.entries(scanInfo)
    if (entries.length > 0) {
      return 'In Progress'
    }
  }

  // If no CDS or AS pipeline status, return a default value
  return 'Scheduled'
}

export const DEFAULT_INSPECTION_TABLE_PANEL_DATA: InspectionTablePanelData = {
  ...DEFAULT_TABLE_PANEL_SHARED_FIELD_DATA,
  title: 'Inspections',
  isPinned: true,
  columns: COLUMN_DEFS,
  groupByType: 'inspection',
  enabledExtraColumnIds: ['col_insp_status'],
}
